import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  ArrowRight,
  AlertTriangle,
  RefreshCw,
  Calendar,
  ClipboardList,
  Users,
  Briefcase,
  MessageSquare,
  Scale,
  FileText,
  BookOpen,
  Package,
  BarChart3,
  ChevronRight,
  GraduationCap,
  Shield,
  Target,
  CheckCircle,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/App";

const iconMap = {
  AlertTriangle,
  RefreshCw,
  Calendar,
  ClipboardList,
  Users,
  Briefcase,
  MessageSquare,
  Scale,
  FileText,
};

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function Home() {
  const [pillars, setPillars] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pillarsRes, protocolsRes] = await Promise.all([
          api.get("/pillars"),
          api.get("/protocols?limit=6"),
        ]);
        setPillars(pillarsRes.data);
        setProtocols(protocolsRes.data);
      } catch (error) {
        // Error fetching home page data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/protocols?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-white text-slate-900 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text Content */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs tracking-widest font-medium text-orange-600 mb-4" data-testid="hero-label">
                PPS PlayBook
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold mb-6 leading-tight text-slate-900" data-testid="hero-title">
                Coherent Student Support. Districtwide.
              </h1>
              <p className="text-lg text-slate-600 mb-4">
                PPS PlayBook helps school districts design and implement coherent, student-centered 
                Pupil Personnel Services, so student support works the same way in every school, every day.
              </p>
              <p className="text-base text-slate-500 mb-8">
                The Playbook provides clear protocols, data guidance, and practical tools that help districts 
                deliver consistent, high-quality student support across schools.
              </p>

              {/* Search */}
              <form onSubmit={handleSearch} className="flex gap-3 max-w-xl mb-8">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search protocols (e.g., 'hospitalization', 'attendance')"
                    className="search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="hero-search-input"
                  />
                </div>
                <Button type="submit" className="btn-primary" data-testid="hero-search-btn">
                  Search
                </Button>
              </form>

              {/* Quick Links */}
              <div className="flex flex-wrap gap-4">
                <Link to="/protocols">
                  <Button variant="outline" className="btn-secondary border-white text-white hover:bg-white hover:text-slate-900" data-testid="browse-protocols-btn">
                    Browse Protocols
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/framework">
                  <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" data-testid="view-framework-btn">
                    View Framework
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right: Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1529390079861-591de354faf5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Students collaborating in small groups"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How PPS PlayBook Works - NEW SECTION */}
      <section className="py-16 lg:py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-12">
            <p className="label-uppercase text-orange-500 mb-2">The Approach</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              How PPS PlayBook works
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              PPS Playbook helps school districts design and implement coherent, student-centered 
              Pupil Personnel Services, so student support works the same way in every school, every day.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="how-it-works">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="relative"
            >
              <div className="bg-slate-50 border border-slate-200 p-6 h-full rounded-sm">
                <div className="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold mb-4">
                  1
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Define the System
                </h3>
                <p className="text-sm text-slate-500 uppercase tracking-wide mb-3">Framework</p>
                <p className="text-sm text-slate-600">
                  The Nine Pillars of Coherent PPS establish a shared, districtwide understanding of 
                  what effective Pupil Personnel Services require. Each pillar identifies a critical 
                  domain, such as crisis response, attendance, LRE decision-making, and equity, where 
                  consistency reduces risk and improves outcomes.
                </p>
                <Link to="/framework" className="inline-flex items-center text-sm font-medium text-orange-600 mt-4 hover:text-orange-700">
                  Explore Framework
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-slate-50 border border-slate-200 p-6 h-full rounded-sm">
                <div className="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold mb-4">
                  2
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Standardize Responses
                </h3>
                <p className="text-sm text-slate-500 uppercase tracking-wide mb-3">Protocols</p>
                <p className="text-sm text-slate-600">
                  The Protocol Library translates the framework into action. Each protocol clearly 
                  defines roles, timelines, documentation requirements, and step-by-step guidance so 
                  responses to common PPS situations are predictable, appropriate, and defensible across all buildings.
                </p>
                <Link to="/protocols" className="inline-flex items-center text-sm font-medium text-orange-600 mt-4 hover:text-orange-700">
                  View Protocols
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <div className="bg-slate-50 border border-slate-200 p-6 h-full rounded-sm">
                <div className="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold mb-4">
                  3
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Assess System Effectiveness
                </h3>
                <p className="text-sm text-slate-500 uppercase tracking-wide mb-3">Data Guides</p>
                <p className="text-sm text-slate-600">
                  The Data Guides outline what data district leaders should review, how to interpret 
                  patterns and variability, and which actions to prioritize in the first 30–90 days. These 
                  guides support proactive leadership and early identification of risk.
                </p>
                <Link to="/data" className="inline-flex items-center text-sm font-medium text-orange-600 mt-4 hover:text-orange-700">
                  Explore Data Guides
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </motion.div>

            {/* Step 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              <div className="bg-slate-50 border border-slate-200 p-6 h-full rounded-sm">
                <div className="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold mb-4">
                  4
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Implement Consistent Practices
                </h3>
                <p className="text-sm text-slate-500 uppercase tracking-wide mb-3">District Use</p>
                <p className="text-sm text-slate-600">
                  Districts integrate PPS Playbook into existing structures to align practices, support 
                  staff training, and promote consistent decision-making while reducing compliance risk. 
                  The Playbook does not replace professional expertise; it creates the conditions for that 
                  expertise to be applied consistently and effectively.
                </p>
                <Link to="/about#services" className="inline-flex items-center text-sm font-medium text-orange-600 mt-4 hover:text-orange-700">
                  Get Support
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Result Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12 bg-emerald-50 border border-emerald-200 p-6 lg:p-8 rounded-sm"
          >
            <div className="flex items-start gap-4">
              <CheckCircle className="w-8 h-8 text-emerald-600 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-slate-700">
                  The result is a school district where student support is consistent across schools, families have 
                  clarity, staff understand expectations, and leadership decisions are informed by data.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Start Here Paths - ELEVATED VISUAL EMPHASIS */}
      <section className="py-16 lg:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-12">
            <p className="label-uppercase text-orange-400 mb-2">Start Here</p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              What brings you here today?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Choose your entry point based on your immediate need.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6" data-testid="start-here-paths">
            {/* I Have a Situation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Link 
                to="/protocols"
                className="block p-8 bg-slate-800 border-2 border-red-500/50 hover:border-red-500 hover:bg-slate-800/80 transition-all duration-300 h-full rounded-sm group"
                data-testid="path-situation"
              >
                <div className="w-14 h-14 bg-red-500/20 rounded-full flex items-center justify-center mb-6 group-hover:bg-red-500/30 transition-colors">
                  <AlertTriangle className="w-7 h-7 text-red-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  "I Have a Situation"
                </h3>
                <p className="text-slate-400 mb-6">
                  Find the appropriate protocol for your scenario, whether it involves crisis response, 
                  re-entry, attendance, referrals, or other student support needs.
                </p>
                <span className="inline-flex items-center text-sm font-medium text-red-400 group-hover:text-red-300">
                  Protocol Library
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>

            {/* I'm Building Systems */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Link 
                to="/framework"
                className="block p-8 bg-slate-800 border-2 border-emerald-500/50 hover:border-emerald-500 hover:bg-slate-800/80 transition-all duration-300 h-full rounded-sm group"
                data-testid="path-systems"
              >
                <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6 group-hover:bg-emerald-500/30 transition-colors">
                  <Layers className="w-7 h-7 text-emerald-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  "I'm building systems"
                </h3>
                <p className="text-slate-400 mb-6">
                  Explore the PPS framework pillars and implementation guidance 
                  for coherent district-wide structures.
                </p>
                <span className="inline-flex items-center text-sm font-medium text-emerald-400 group-hover:text-emerald-300">
                  Nine Pillars Framework
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>

            {/* I Need to Diagnose */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Link 
                to="/data"
                className="block p-8 bg-slate-800 border-2 border-blue-500/50 hover:border-blue-500 hover:bg-slate-800/80 transition-all duration-300 h-full rounded-sm group"
                data-testid="path-diagnose"
              >
                <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-500/30 transition-colors">
                  <BarChart3 className="w-7 h-7 text-blue-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  "I need to diagnose"
                </h3>
                <p className="text-slate-400 mb-6">
                  Data guides that tell you what to pull, what it means, 
                  and what to do in the first 90 days.
                </p>
                <span className="inline-flex items-center text-sm font-medium text-blue-400 group-hover:text-blue-300">
                  Data Guides
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Framework Pillars */}
      <section className="py-16 lg:py-24 bg-slate-50 dot-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
            <div>
              <p className="label-uppercase text-orange-500 mb-2">PPS Framework</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">
                Nine Pillars of Coherent PPS
              </h2>
            </div>
            <Link to="/framework" className="mt-4 lg:mt-0">
              <Button variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="view-all-pillars-btn">
                View All Pillars
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pillars-grid">
            {!loading && pillars.slice(0, 6).map((pillar, index) => {
              const Icon = iconMap[pillar.icon] || FileText;
              return (
                <motion.div
                  key={pillar.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/framework/${pillar.slug}`}
                    className="block bg-white border border-slate-200 p-6 hover:shadow-lg transition-all duration-300"
                    style={{ borderLeftColor: pillar.color, borderLeftWidth: "4px" }}
                    data-testid={`pillar-card-${pillar.slug}`}
                  >
                    <Icon 
                      className="w-8 h-8 mb-4" 
                      style={{ color: pillar.color }}
                      strokeWidth={1.5} 
                    />
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      {pillar.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {pillar.description}
                    </p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Protocols */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
            <div>
              <p className="label-uppercase text-orange-500 mb-2">Protocol Library</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">
                Featured Protocols
              </h2>
            </div>
            <Link to="/protocols" className="mt-4 lg:mt-0">
              <Button variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="view-all-protocols-btn">
                View All Protocols
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="space-y-4" data-testid="featured-protocols">
            {!loading && protocols.slice(0, 4).map((protocol, index) => (
              <motion.div
                key={protocol.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/protocols/${protocol.slug}`}
                  className="protocol-card block bg-white border border-slate-200 p-6 hover:border-slate-300"
                  data-testid={`protocol-card-${protocol.slug}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`badge-urgency-${protocol.urgency} px-3 py-1 rounded-full text-xs font-mono uppercase`}>
                          {protocol.urgency}
                        </span>
                        <span className="text-xs text-slate-500 font-mono">
                          {protocol.applicable_settings?.join(" / ")}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">
                        {protocol.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-1">
                        {protocol.summary}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 hidden lg:block" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
